import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import HeaderBar from '../components/HeaderBar';
import './MeetingRoomBooking.css';

// Helper to format date for display
const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const parsedDate = new Date(`${dateString}T00:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return dateString;
    return parsedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

// Helper to get YYYY-MM-DD in local time
const getLocalDateStr = (date) => {
    const d = date || new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function MeetingRoomBooking({ user, onLogout }) {
    const navigate = useNavigate();

    // Steps: category-select, room-select, date-select, time-select, confirm, ticket
    const [step, setStep] = useState('category-select');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [durationOptions, setDurationOptions] = useState([]);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [reservationTitle, setReservationTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [bookingResult, setBookingResult] = useState(null);
    const [roomMeetings, setRoomMeetings] = useState({});


    const [showOverlapModal, setShowOverlapModal] = useState(false);
    const [overlapData, setOverlapData] = useState(null);
    const [pendingWaitlist, setPendingWaitlist] = useState(false);

    // Fetch today's meetings for a room
    const fetchRoomMeetings = useCallback(async (roomId) => {
        try {
            const response = await axios.get(`/api/meeting-rooms/${roomId}/todays-meetings`);
            setRoomMeetings(prev => ({ ...prev, [roomId]: response.data || [] }));
        } catch (err) {
            console.error('Failed to load room meetings:', err);
        }
    }, []);

    useEffect(() => {
        // Fetch meetings for all rooms when rooms are loaded
        if (rooms.length > 0) {
            rooms.forEach(room => fetchRoomMeetings(room.id));
        }
    }, [rooms, fetchRoomMeetings]);

    const fetchRooms = useCallback(async (category = null) => {
        try {
            setLoading(true);
            const url = category
                ? `/api/meeting-rooms?category=${category}`
                : '/api/meeting-rooms';
            const response = await axios.get(url);
            setRooms(response.data || []);
        } catch (err) {
            setError('Failed to load spaces');
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch duration options
    const fetchDurationOptions = useCallback(async () => {
        try {
            const response = await axios.get('/api/meeting-rooms/duration-options');
            setDurationOptions(response.data || []);
        } catch (err) {
            console.error('Failed to load duration options');
        }
    }, []);

    // Fetch availability for selected room and date
    const fetchAvailability = useCallback(async (roomId, date) => {
        if (!roomId || !date) return;
        try {
            setLoading(true);
            const response = await axios.get(`/api/meeting-rooms/${roomId}/availability?date=${date}`);
            setAvailability(response.data);
        } catch (err) {
            setError('Failed to load room availability');
        } finally {
            setLoading(false);
        }
    }, []);

    // Check for overlap when time is selected
    const checkOverlap = useCallback(async (start, end) => {
        if (!selectedRoom || !selectedDate || !start || !end) return;

        try {
            const response = await axios.get(`/api/meeting-rooms/${selectedRoom.id}/check-overlap`, {
                params: {
                    date: selectedDate,
                    start,
                    end
                }
            });

            if (response.data.hasConflict) {
                setOverlapData(response.data);
                setShowOverlapModal(true);
            } else {
                setPendingWaitlist(false);
                setShowOverlapModal(false);
            }
        } catch (err) {
            console.error('Failed to check overlap:', err);
        }
    }, [selectedRoom, selectedDate]);

    // Effect to check overlap when times change
    useEffect(() => {
        if (selectedStartTime && selectedEndTime) {
            checkOverlap(selectedStartTime, selectedEndTime);
        }
    }, [selectedStartTime, selectedEndTime, checkOverlap]);

    useEffect(() => {
        fetchRooms();
        fetchDurationOptions();
    }, [fetchRooms, fetchDurationOptions]);

    // Handle category selection
    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setError('');
        fetchRooms(category);
        setStep('room-select');
    };

    // Handle room selection
    const handleRoomSelect = (room) => {
        setSelectedRoom(room);
        setError('');
        setStep('date-select');
    };

    // Handle date selection
    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setError('');
        fetchAvailability(selectedRoom.id, date);
        setStep('time-select');
    };

    // Handle time selection
    const handleTimeSelect = (startTime, endTime) => {
        setSelectedStartTime(startTime);
        setSelectedEndTime(endTime);
        setError('');

        // Wait for overlap check before proceeding
        // Note: The check logic is in useEffect, but we need to prevent sync transition
        // if there's a conflict.

        // Actually, users click "Next" button to proceed. 
        // We should disable "Next" or handle the overlap in the flow.
        // We will let the modal intercept.
        setStep('confirm');
    };

    // Handle waitlist join from modal
    const handleJoinWaitlist = () => {
        setPendingWaitlist(true);
        setShowOverlapModal(false);
        setStep('confirm');
    };

    // Handle cancel from modal
    const handleCancelOverlap = () => {
        setShowOverlapModal(false);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setPendingWaitlist(false);
    };

    // Handle booking confirmation
    const handleConfirmBooking = async () => {
        try {
            setLoading(true);
            setError('');

            // Validate reservation title
            if (!reservationTitle || !reservationTitle.trim()) {
                setError('Please enter a reservation title');
                setLoading(false);
                return;
            }

            const response = await axios.post('/api/meeting-rooms/book', {
                room_id: selectedRoom.id,
                booking_date: selectedDate,
                start_time: selectedStartTime,
                end_time: selectedEndTime,
                meeting_title: reservationTitle.trim(),
                force_waitlist: pendingWaitlist
            });

            setBookingResult(response.data.booking);
            setSuccess(pendingWaitlist
                ? 'Added to Waiting List successfully!'
                : 'Reservation confirmed successfully!');
            setStep('ticket');
        } catch (err) {
            setError(err.response?.data?.error || 'Booking failed');
        } finally {
            setLoading(false);
        }
    };

    // Handle back navigation
    const handleBack = () => {
        switch (step) {
            case 'room-select':
                setStep('category-select');
                setSelectedCategory(null);
                setRooms([]);
                break;
            case 'date-select':
                setStep('room-select');
                setSelectedRoom(null);
                break;
            case 'time-select':
                setStep('date-select');
                setSelectedDate(null);
                setAvailability(null);
                break;
            case 'confirm':
                setStep('time-select');
                // Don't clear times immediately so user can adjust
                setReservationTitle('');
                setPendingWaitlist(false);
                break;
            default:
                navigate('/booking');
        }
        setError('');
    };

    // Reset wizard
    const resetWizard = () => {
        setStep('room-select');
        setSelectedRoom(null);
        setSelectedDate(null);
        setAvailability(null);
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setReservationTitle('');
        setBookingResult(null);
        setError('');
        setSuccess('');
        setPendingWaitlist(false);
    };

    // Generate next 14 days for date selection
    const getDateOptions = () => {
        const dates = [];
        const today = new Date();
        for (let i = 0; i < 14; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            const dow = date.getDay();
            // Skip weekends
            if (dow !== 0 && dow !== 6) {
                dates.push({
                    dateStr: getLocalDateStr(date),
                    dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
                    dayNum: date.getDate(),
                    month: date.toLocaleDateString('en-US', { month: 'short' }),
                    isToday: i === 0
                });
            }
        }
        return dates;
    };

    // Generate time slots for start/end time selection
    const generateTimeSlots = () => {
        const slots = [];
        for (let hour = 7; hour <= 17; hour++) {
            slots.push(`${String(hour).padStart(2, '0')}:00`);
            if (hour < 17) {
                slots.push(`${String(hour).padStart(2, '0')}:30`);
            }
        }
        return slots;
    };

    return (
        <div className="meeting-room-booking">
            <HeaderBar user={user} onLogout={onLogout} />

            {/* Overlap Modal */}
            {showOverlapModal && overlapData && (
                <div className="mrb-modal-overlay">
                    <div className="mrb-modal">
                        <div className="mrb-modal-header">
                            <h3>⚠️ Time Slot Conflict</h3>
                        </div>
                        <div className="mrb-modal-body">
                            <p>This time slot overlaps with an existing reservation.</p>

                            <div className="overlap-info-box">
                                <div className="overlap-row">
                                    <span>Overlap Duration:</span>
                                    <strong>{overlapData.overlap_duration}</strong>
                                </div>
                                <div className="overlap-row">
                                    <span>Booked By:</span>
                                    <span>{overlapData.booked_by_name}</span>
                                </div>
                                <div className="overlap-row">
                                    <span>Booked Slot:</span>
                                    <span>{overlapData.booked_start} - {overlapData.booked_end}</span>
                                </div>
                            </div>

                            <p>Would you like to join the <strong>Waiting List</strong>? We'll notify you if the slot becomes available.</p>
                        </div>
                        <div className="mrb-modal-footer">
                            <button onClick={handleCancelOverlap} className="btn-modal-cancel">
                                Choose Different Time
                            </button>
                            <button onClick={handleJoinWaitlist} className="btn-modal-waitlist">
                                Join Waiting List
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="mrb-container">
                {error && <div className="mrb-error">{error}</div>}
                {success && <div className="mrb-success">{success}</div>}

                <div className="mrb-content">
                    {/* Step 0: Category Selection */}
                    {step === 'category-select' && (
                        <div className="mrb-step">
                            <h2>📍 Step 1: Select Space Type</h2>
                            <p className="mrb-subtitle">Choose the type of space you need</p>

                            <div className="mrb-category-grid">
                                <button
                                    className="mrb-category-card"
                                    onClick={() => handleCategorySelect('collaboration_pod')}
                                >
                                    <div className="category-icon-image-wrapper">
                                        <img
                                            src="/room-images/colpod1.jpeg"
                                            alt="Collaboration Pod"
                                            className="category-icon-image"
                                        />
                                    </div>
                                    <h3>Collaboration Pod</h3>
                                    <p className="category-desc">2 person collaboration spaces</p>
                                    <ul className="category-features">
                                        <li>✓ 4 spaces available</li>
                                        <li>✓ West Wing: W1, W2</li>
                                        <li>✓ East Wing: E1, E2</li>
                                        <li>✓ 24 inch Monitor</li>
                                        <li>✓ Suitable for team discussion</li>
                                    </ul>
                                </button>

                                <button
                                    className="mrb-category-card"
                                    onClick={() => handleCategorySelect('phone_booth')}
                                >
                                    <div className="category-icon-image-wrapper">
                                        <img
                                            src="/room-images/pbooth.jpeg"
                                            alt="Phone Booth"
                                            className="category-icon-image"
                                        />
                                    </div>
                                    <h3>Phone Booth</h3>
                                    <p className="category-desc">1 person privacy booths</p>
                                    <ul className="category-features">
                                        <li>✓ 6 spaces available</li>
                                        <li>✓ West Wing: W1, W2, W3</li>
                                        <li>✓ East Wing: E1, E2, E3</li>
                                        <li>✓ Perfect for calls & focus work</li>
                                    </ul>
                                </button>
                            </div>

                            <div className="mrb-nav">
                                <button onClick={() => navigate('/book')} className="btn-back">
                                    ← Back to Booking Options
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Room Selection */}
                    {step === 'room-select' && (
                        <div className="mrb-step">
                            <h2>📍 Step 2: Select {selectedCategory === 'collaboration_pod' ? 'Collaboration Pod' : 'Phone Booth'}</h2>
                            <p className="mrb-subtitle">Choose a specific room for your booking</p>

                            {loading ? (
                                <div className="mrb-loading">Loading rooms...</div>
                            ) : rooms.length === 0 ? (
                                <div className="mrb-empty">
                                    <p>No spaces available.</p>
                                    <p>Contact admin to add spaces.</p>
                                </div>
                            ) : (
                                <div className="mrb-room-grid">
                                    {rooms.map(room => {
                                        // Map room booth_id to image filenames in /room-images/
                                        let imageUrl = '/data/default-room.jpg'; // fallback

                                        if (room.booth_id) {
                                            // Check for specific room images (CPod-W1.jpg, CPod-W2.jpg, etc.)
                                            const roomImagePath = `/room-images/${room.booth_id}.jpg`;
                                            imageUrl = roomImagePath;
                                        }

                                        return (
                                            <div key={room.id} className="mrb-room-card-wrapper">
                                                <button
                                                    className="mrb-room-card"
                                                    onClick={() => handleRoomSelect(room)}
                                                    style={{
                                                        backgroundImage: `url('${imageUrl}')`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                        backgroundRepeat: 'no-repeat'
                                                    }}
                                                >
                                                </button>
                                                <div className="mrb-room-content">
                                                    <div className="room-name">{room.label || room.booth_id}</div>
                                                    <div className="room-id">{room.booth_id}</div>


                                                    {/* Meetings List */}
                                                    {roomMeetings[room.id] && (
                                                        <div className="room-meetings-list">
                                                            {(() => {
                                                                const today = new Date().toISOString().split('T')[0];
                                                                const todayMeetings = roomMeetings[room.id].filter(m => m.booking_date === today);
                                                                const futureMeetings = roomMeetings[room.id].filter(m => m.booking_date > today);

                                                                return (
                                                                    <>
                                                                        {/* Today's Meetings Section */}
                                                                        <div className="meetings-header">Today's Reservations:</div>
                                                                        {todayMeetings.length > 0 ? (
                                                                            todayMeetings.map((meeting, idx) => (
                                                                                <div key={meeting.id} className="meeting-item">
                                                                                    <div className="meeting-number">{idx + 1}.</div>
                                                                                    <div className="meeting-details">
                                                                                        <div className="meeting-user">{meeting.user_name || meeting.user_email}</div>
                                                                                        {meeting.meeting_title && (
                                                                                            <div className="meeting-title">{meeting.meeting_title}</div>
                                                                                        )}
                                                                                        <div className="meeting-time">
                                                                                            {meeting.start_time} - {meeting.end_time}
                                                                                            {meeting.status === 'pending' && <span className="waitlist-tag"> (Waitlist)</span>}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            ))
                                                                        ) : (
                                                                            <div className="no-meetings">- No Booking for Today</div>
                                                                        )}

                                                                        {/* Future Meetings Section */}
                                                                        {futureMeetings.length > 0 && (
                                                                            <>
                                                                                <div className="meetings-header" style={{ marginTop: '12px' }}>Next Booking</div>
                                                                                <div className="meeting-date-header">
                                                                                    {new Date(futureMeetings[0].booking_date + 'T00:00:00').toLocaleDateString('en-US', {
                                                                                        weekday: 'long',
                                                                                        day: 'numeric',
                                                                                        month: 'short',
                                                                                        year: 'numeric'
                                                                                    })}
                                                                                </div>
                                                                                {futureMeetings.map((meeting, idx) => (
                                                                                    <div key={meeting.id} className="meeting-item">
                                                                                        <div className="meeting-number">{idx + 1}.</div>
                                                                                        <div className="meeting-details">
                                                                                            <div className="meeting-user">{meeting.user_name || meeting.user_email}</div>
                                                                                            {meeting.meeting_title && (
                                                                                                <div className="meeting-title">{meeting.meeting_title}</div>
                                                                                            )}
                                                                                            <div className="meeting-time">
                                                                                                {meeting.start_time} - {meeting.end_time}
                                                                                                {meeting.status === 'pending' && <span className="waitlist-tag"> (Waitlist)</span>}
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}

                                </div>
                            )}

                            <div className="mrb-nav">
                                <button onClick={() => navigate('/book')} className="btn-back">
                                    ← Back to Booking Options
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Date Selection */}
                    {step === 'date-select' && (
                        <div className="mrb-step">
                            <h2>📅 Step 3: Select Date</h2>
                            <p className="mrb-subtitle">
                                Booking: <strong>{selectedRoom?.label || selectedRoom?.booth_id}</strong>
                            </p>

                            <div className="mrb-date-grid">
                                {getDateOptions().map(date => (
                                    <button
                                        key={date.dateStr}
                                        className={`mrb-date-card ${date.isToday ? 'today' : ''}`}
                                        onClick={() => handleDateSelect(date.dateStr)}
                                    >
                                        <div className="date-day">{date.dayName}</div>
                                        <div className="date-num">{date.dayNum}</div>
                                        <div className="date-month">{date.month}</div>
                                        {date.isToday && <span className="today-badge">Today</span>}
                                    </button>
                                ))}
                            </div>

                            <div className="mrb-nav">
                                <button onClick={handleBack} className="btn-back">
                                    ← Back
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Select Time Range */}
                    {step === 'time-select' && (
                        <div className="mrb-step">
                            <h2>⏰ Step 4: Select Time Range</h2>
                            <p className="mrb-subtitle">
                                {selectedRoom?.label} on {formatDisplayDate(selectedDate)}
                            </p>

                            <div className="time-range-selector">
                                <div className="time-select-group">
                                    <label htmlFor="start-time">Start Time:</label>
                                    <select
                                        id="start-time"
                                        className="time-select"
                                        value={selectedStartTime || ''}
                                        onChange={(e) => setSelectedStartTime(e.target.value)}
                                    >
                                        <option value="">Select start time</option>
                                        {generateTimeSlots().slice(0, -1).map(time => (
                                            <option key={time} value={time}>{time}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="time-select-group">
                                    <label htmlFor="end-time">End Time:</label>
                                    <select
                                        id="end-time"
                                        className="time-select"
                                        value={selectedEndTime || ''}
                                        onChange={(e) => setSelectedEndTime(e.target.value)}
                                        disabled={!selectedStartTime}
                                    >
                                        <option value="">Select end time</option>
                                        {selectedStartTime && generateTimeSlots()
                                            .filter(time => time > selectedStartTime && time <= '17:00')
                                            .map(time => (
                                                <option key={time} value={time}>{time}</option>
                                            ))}
                                    </select>
                                </div>

                                {selectedStartTime && selectedEndTime && (
                                    <div className="selected-duration">
                                        <strong>Duration:</strong> {selectedStartTime} - {selectedEndTime}
                                    </div>
                                )}
                            </div>

                            {/* Show existing bookings */}
                            {loading ? (
                                <div className="mrb-loading">Loading availability...</div>
                            ) : availability?.bookings?.length > 0 && (
                                <div className="mrb-existing-bookings">
                                    <h4>
                                        {selectedDate === new Date().toISOString().split('T')[0]
                                            ? "Today's Bookings:"
                                            : `Bookings for ${formatDisplayDate(selectedDate)}:`}
                                    </h4>
                                    {availability.bookings.map(b => (
                                        <div key={b.id} className="existing-booking">
                                            <div>{b.start_time} - {b.end_time}</div>
                                            {b.user_email && (
                                                <div className="booking-user">
                                                    {b.user_email}
                                                    {b.status === 'pending' && <span className="waitlist-tag"> (Waiting List)</span>}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mrb-nav">
                                <button onClick={handleBack} className="btn-back">
                                    ← Back
                                </button>
                                <button
                                    onClick={() => selectedStartTime && selectedEndTime && handleTimeSelect(selectedStartTime, selectedEndTime)}
                                    className="btn-next"
                                    disabled={!selectedStartTime || !selectedEndTime || showOverlapModal}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Confirmation */}
                    {step === 'confirm' && (
                        <div className="mrb-step">
                            <h2>{pendingWaitlist ? '⏳ Step 5: Confirm Waitlist Request' : '✅ Step 5: Confirm Booking'}</h2>

                            {pendingWaitlist && (
                                <div style={{
                                    background: '#fff7ed',
                                    border: '1px solid #fed7aa',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '20px',
                                    color: '#c2410c'
                                }}>
                                    <strong>Note:</strong> This time slot is currently booked. You will be added to the waiting list and notified if it becomes available.
                                </div>
                            )}

                            <div className="mrb-confirm-card">
                                <div className="confirm-row">
                                    <span className="confirm-label">Room:</span>
                                    <span className="confirm-value">{selectedRoom?.label || selectedRoom?.booth_id}</span>
                                </div>
                                <div className="confirm-row">
                                    <span className="confirm-label">Date:</span>
                                    <span className="confirm-value">{formatDisplayDate(selectedDate)}</span>
                                </div>
                                <div className="confirm-row">
                                    <span className="confirm-label">Time:</span>
                                    <span className="confirm-value">
                                        {selectedStartTime} - {selectedEndTime}
                                    </span>
                                </div>
                                <div className="confirm-row">
                                    <span className="confirm-label">Booked by:</span>
                                    <span className="confirm-value">{user?.name || user?.email}</span>
                                </div>
                                {pendingWaitlist && (
                                    <div className="confirm-row" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                                        <span className="confirm-label">Type:</span>
                                        <span className="confirm-value" style={{ color: '#fbbf24' }}>WAITING LIST</span>
                                    </div>
                                )}

                                {/* Meeting Title Input */}
                                <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '20px' }}>
                                    <div className="form-group">
                                        <label htmlFor="meeting-title">Reservation Title *</label>
                                        <input
                                            id="meeting-title"
                                            type="text"
                                            value={reservationTitle}
                                            onChange={(e) => setReservationTitle(e.target.value)}
                                            placeholder="e.g., Team Discussion, Focus Work"
                                            className="input-field"
                                            maxLength={100}
                                        />
                                        <p className="hint-text">Please provide a brief title for this reservation</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mrb-nav confirm-nav">
                                <button onClick={handleBack} className="btn-back">
                                    ← Back
                                </button>
                                <button
                                    onClick={handleConfirmBooking}
                                    className="btn-confirm"
                                    disabled={loading || !reservationTitle.trim()}
                                    style={pendingWaitlist ? { background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' } : {}}
                                >
                                    {loading ? 'Processing...' : (pendingWaitlist ? 'Join Waiting List' : 'Confirm Booking')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Ticket */}
                    {step === 'ticket' && bookingResult && (
                        <div className="mrb-step">
                            <div className="mrb-ticket">
                                <div className="ticket-header" style={pendingWaitlist || bookingResult.status === 'pending' ? { background: '#f59e0b22' } : {}}>
                                    <h2>{bookingResult.status === 'pending' ? '⏳ Application Received' : '🎫 Booking Confirmed!'}</h2>
                                </div>
                                <div className="ticket-body">
                                    {bookingResult.status === 'pending' && (
                                        <div style={{ marginBottom: '15px', color: '#fbbf24', fontWeight: 'bold' }}>
                                            You have been added to the waiting list.
                                        </div>
                                    )}
                                    <div className="ticket-room">{bookingResult.room_name}</div>
                                    {bookingResult.meeting_title && (
                                        <div className="ticket-meeting-title">{bookingResult.meeting_title}</div>
                                    )}
                                    <div className="ticket-date">{formatDisplayDate(bookingResult.booking_date)}</div>
                                    <div className="ticket-time">
                                        {bookingResult.start_time} - {bookingResult.end_time}
                                    </div>
                                    <div className="ticket-user">{user?.name || user?.email}</div>
                                    <div className="ticket-id">Booking ID: #{bookingResult.id}</div>
                                </div>
                            </div>

                            <div className="mrb-nav">
                                <button onClick={resetWizard} className="btn-new-booking">
                                    Book Another Room
                                </button>
                                <button onClick={() => navigate('/book')} className="btn-dashboard">
                                    Back to Home
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
}

export default MeetingRoomBooking;
